import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useIsTextEnlarged } from "@/hooks/useIsTextEnlarged";
import SystemButton from "../SystemButton/SystemButton";

/** What a footer button does to its step, which decides how loudly it is drawn. */
export type SheetStepButtonTone = "dismiss" | "confirm" | "delete";

type Props = {
	/**
	 * - `dismiss`: leaves without changing anything (Close, Cancel, Back, Not now). Outlined in a light neutral hairline,
	 *   so it reads as a button but never competes with the step's content or with the commit beside it.
	 * - `confirm`: the step's one commit (Save, Add, Post, Done). Filled with the brand.
	 * - `delete`: a commit that removes or destroys. Filled red.
	 */
	tone: SheetStepButtonTone;
	/** The label, already translated. Ellipsized rather than overflowing when the row is narrow. */
	children: ReactNode;
	onClick: () => void;
	disabled?: boolean;
	loading?: boolean;
	dataTestId: string;
};

const VARIANT_BY_TONE = {
	dismiss: "hairline",
	confirm: "primary",
	delete: "delete",
} as const satisfies Record<SheetStepButtonTone, string>;

/**
 * How a footer shares its width: a commit takes two parts to a dismiss's one, because it is where the step is going.
 * On its own, either one fills the row. At Android's larger font sizes a third of the row no longer holds "Cancel",
 * so every button takes an equal share.
 */
const FLEX_BY_TONE: Record<SheetStepButtonTone, number> = { dismiss: 1, confirm: 2, delete: 2 };
const ENLARGED_TEXT_FLEX = 1;

/**
 * A button in a `SheetStep` footer. It sets the footer's density and emphasis, so every step's footer is drawn the
 * same way.
 *
 * The footer is sticky, so it stays on screen under every step. At the old weight (a 48px dark outline on every Close
 * and Back) it was the heaviest thing on the sheet even when it only said "Close". So it is set at the field height
 * (`size="small"`, `CONTROL_HEIGHT.STANDARD`) and grows to `TOUCH_TARGET_MIN` on coarse pointers only. The way out
 * gets SystemButton's light `hairline` outline, and only a commit is filled. A disabled commit is painted neutral
 * (`mutedWhenDisabled`), so it cannot be mistaken for a live one.
 */
export const SheetStepButton: FC<Props> = ({ tone, children, onClick, disabled, loading, dataTestId }) => {
	const isTextEnlarged = useIsTextEnlarged();

	return (
		<SystemButton
			buttonVariant={VARIANT_BY_TONE[tone]}
			size="small"
			touchTarget
			mutedWhenDisabled={tone !== "dismiss"}
			onClick={onClick}
			disabled={disabled}
			loading={loading}
			data-testid={dataTestId}
			// `minWidth: 0` releases SystemButton's `fit-content` floor so a long label on a 320px phone ellipsizes inside
			// its share instead of pushing the pair past the sheet's edge.
			sx={{ flex: isTextEnlarged ? ENLARGED_TEXT_FLEX : FLEX_BY_TONE[tone], minWidth: 0 }}
		>
			<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
				{children}
			</Box>
		</SystemButton>
	);
};
